import { MouseEvent } from 'react'

import {
  IconLoader2,
  IconPencil,
  IconPhotoPlus,
  IconTrash,
  IconZoomIn,
} from '@tabler/icons-react'
import { Image } from 'antd'
import Link from 'next/link'
import styled, { keyframes } from 'styled-components'

import { Text } from './BUI/Text'
import { Flexbox } from './Flexbox'

import { FileUploadResult } from '@/hooks/useAttachmentUpload'
import {
  extensionFromUrl,
  getDocumentIcon,
  isDocument,
} from '@/modules/attachments/attachmentHelpers'

import { buildPassTheme } from 'src/styles/theme'

const Files = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
`

const File = styled.div`
  position: relative;
  color: ${({ theme }) => theme.colour.secondary._400};
  background-color: ${({ theme }) => theme.colour.secondary._50};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background-color: rgba(255, 255, 255, 0.3);
    box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.2);
  }
`

const Remove = styled.button.attrs({ type: 'button' })`
  position: absolute;
  bottom: -0.375rem;
  right: -0.375rem;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: ${({ theme }) => theme.colour.secondary.base};
  background-color: ${({ theme }) => theme.colour.secondary._50};
  border: 2px solid white;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`

const TruncateText = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 90px;
`

const Edit = styled(Remove)`
  top: -0.375rem;
`

const Spin = keyframes`
	100% {
		transform: rotate(1turn);
  } 
`

const Loading = styled.div`
  animation: ${Spin} 1s linear infinite;
  display: flex;
  justify-content: center;
  align-items: center;
`

const Progress = styled(Text)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

export type Attachment = {
  uid?: string
  url: string
  alt: string
  previewUrl?: string
  percent?: number
  onRemove?: (e: MouseEvent) => void
  onEdit?: () => void
  onClick?: (e: MouseEvent<HTMLImageElement>) => void
}

type AttachmentBoxProps = {
  width: number
  height: number
  attachment: Attachment
  renderingInPdf?: boolean
  children?: React.ReactNode
  style?: React.CSSProperties
  onClick?: () => void
}

export const AttachmentBox: React.FC<AttachmentBoxProps> = ({
  width,
  height,
  attachment,
  renderingInPdf,
  onClick,
  style,
  children,
}) => {
  const fileStyle = { ...style, width: `${width}px`, height: `${height}px` }

  if (children) {
    return (
      <File style={fileStyle} onClick={onClick}>
        {children}
      </File>
    )
  }

  if (!attachment.url) {
    return (
      <File style={fileStyle} onClick={onClick}>
        <Flexbox flexDirection="column" gap="0.5rem" alignItems="center">
          <Loading>
            <IconLoader2
              size={width - 20}
              color={buildPassTheme.colour.secondary._100}
            />
          </Loading>
          {attachment.percent !== undefined && (
            <Progress variant="caption02">{attachment.percent}%</Progress>
          )}
          {attachment.onRemove && (
            <Remove
              onClick={(e) => {
                e.stopPropagation()
                attachment.onRemove(e)
              }}
            >
              <IconTrash size={12} />
            </Remove>
          )}
        </Flexbox>
      </File>
    )
  }

  const isDoc = isDocument(attachment.url)

  if (isDoc) {
    const extension = extensionFromUrl(attachment.url)

    return (
      <Link target="_blank" href={attachment.url}>
        <File style={{ ...fileStyle, padding: '0.25rem', textAlign: 'center' }}>
          <Flexbox flexDirection="column" gap="0.5rem" alignItems="center">
            {getDocumentIcon(extension, undefined, 1.5)}
            {height > 64 && (
              <TruncateText variant="caption01">
                {attachment?.alt?.indexOf('.') > 0
                  ? `${attachment.alt.split('.')[0].slice(0, 7)}...${extension}`
                  : attachment.alt}
              </TruncateText>
            )}
          </Flexbox>
          {attachment.onRemove && (
            <Remove onClick={attachment.onRemove}>
              <IconTrash size={12} />
            </Remove>
          )}
          {attachment.onEdit && (
            <Edit onClick={attachment.onEdit}>
              <IconPencil size={12} />
            </Edit>
          )}
        </File>
      </Link>
    )
  }

  return (
    <File style={fileStyle} onClick={onClick}>
      {renderingInPdf ? (
        <Link target="_blank" href={attachment.previewUrl || attachment.url}>
          <Image
            width={width}
            height={height}
            alt={attachment.alt}
            src={attachment.url}
            preview={false}
            style={{ objectFit: 'contain' }}
          />
        </Link>
      ) : (
        <Image
          width={width}
          height={height}
          src={attachment.url}
          alt={attachment.alt}
          preview={{
            src: attachment.previewUrl || attachment.url,
            mask: <IconZoomIn size={16} />,
          }}
          style={{ objectFit: 'contain' }}
          onClick={attachment.onClick}
        />
      )}

      {attachment.onRemove && (
        <Remove
          onClick={(e) => {
            e.stopPropagation()
            attachment.onRemove(e)
          }}
        >
          <IconTrash size={14} />
        </Remove>
      )}
      {attachment.onEdit && (
        <Edit
          onClick={(e) => {
            e.stopPropagation()
            attachment.onEdit()
          }}
        >
          <IconPencil size={14} />
        </Edit>
      )}
    </File>
  )
}

type MultipleAttachmentBoxProps = {
  attachments: Attachment[]
  width: number
  height: number
  className?: string
  children?: React.ReactNode
  renderingInPdf?: boolean
  onAddClick?: () => void
}

export const AttachmentBoxes: React.FC<MultipleAttachmentBoxProps> = ({
  attachments,
  width,
  height,
  className,
  children,
  renderingInPdf,
  onAddClick,
}) => {
  return (
    <Image.PreviewGroup>
      <Files className={className}>
        {attachments?.map((attachment) => (
          <AttachmentBox
            key={attachment.uid ?? attachment.url ?? attachment.alt}
            width={width}
            height={height}
            attachment={attachment}
            renderingInPdf={renderingInPdf}
          />
        ))}
        {onAddClick && (
          <AttachmentBox
            key="add"
            attachment={null}
            width={width}
            height={height}
            onClick={onAddClick}
            style={{ cursor: 'pointer' }}
          >
            <IconPhotoPlus
              size={24}
              color={buildPassTheme.colour.secondary._500}
            />
          </AttachmentBox>
        )}
        {children}
      </Files>
    </Image.PreviewGroup>
  )
}

type AttachmentFromUploaderProps = {
  file: FileUploadResult
  width: number
  height: number
  onRemove?: (file: FileUploadResult) => void
  onEdit?: (file: FileUploadResult) => void
}

export const AttachmentBoxFromUploader: React.FC<
  AttachmentFromUploaderProps
> = ({ file, width, height, onRemove, onEdit }) => {
  return (
    <AttachmentBox
      width={width}
      height={height}
      attachment={{
        url: file.url,
        alt: file.name,
        percent: file.percent,
        onRemove: () => onRemove?.(file),
        onEdit: onEdit ? () => onEdit(file) : undefined,
      }}
    />
  )
}

type MultipleAttachmentBoxFromUploaderProps = {
  files: FileUploadResult[]
  width: number
  height: number
  onRemove?: (file: FileUploadResult) => void
  onEdit?: (file: FileUploadResult) => void
  className?: string
  children?: React.ReactNode
}

export const AttachmentBoxesFromUploader: React.FC<
  MultipleAttachmentBoxFromUploaderProps
> = ({ files, width, height, onRemove, onEdit, className, children }) => {
  return (
    <Image.PreviewGroup>
      <Files className={className}>
        {files.map((file, index) => (
          <AttachmentBoxFromUploader
            key={`${index}-${file.name}`}
            file={file}
            width={width}
            height={height}
            onRemove={onRemove}
            onEdit={onEdit}
          />
        ))}
        {children}
      </Files>
    </Image.PreviewGroup>
  )
}
